// The Vocabulary tab's "Transitions" view: the little words that join ideas
// when Brazilians talk — sequencing a story, contrasting, softening an
// explanation, checking in. Grouped by function (the order below is the
// teaching order), never alphabetically — and small groups are folded into a
// neighbour that does a similar job, so no table is three terms or fewer. Every example is something a Brazilian
// would say out loud.
//
// Register tags (spoken / written / regional / young; none = neutral, fine
// everywhere) are kept in the data but not shown. `mustKnow` marks the few to
// teach first — they get a ★ in the table.

export const TRANSITION_GROUPS = [
  { id: 'story', label: 'Moving a story along' },
  { id: 'adding', label: 'Adding and concluding' },
  { id: 'contrasting', label: 'Contrasting' },
  { id: 'explaining', label: 'Explaining and softening' },
  { id: 'time', label: 'Time clauses' },
  { id: 'reactions', label: 'Reactions and check-ins' },
] as const

export type TransitionGroup = (typeof TRANSITION_GROUPS)[number]['id']
export type TransitionRegister = 'spoken' | 'written' | 'regional' | 'young'

export type Transition = {
  term: string
  gloss: string
  example: string
  // English for reference, shown under the example.
  exampleEn: string
  group: TransitionGroup
  registers: TransitionRegister[]
  // Where a "regional" term is used ("SP / South").
  region?: string
  // One short usage aside, shown under the gloss.
  note?: string
  mustKnow: boolean
}

type Options = { registers?: TransitionRegister[]; region?: string; note?: string; mustKnow?: boolean }

function row(group: TransitionGroup, term: string, gloss: string, example: string, exampleEn: string, options: Options = {}): Transition {
  return { term, gloss, example, exampleEn, group, registers: options.registers ?? [], region: options.region, note: options.note, mustKnow: options.mustKnow ?? false }
}

const SPOKEN: Options = { registers: ['spoken'] }

export const TRANSITIONS: Transition[] = [
  // Moving a story along: sequencing, then the asides that shift or close a topic
  row('story', 'aí', 'then / so', 'Aí eu falei pra ela...', 'Then I told her...', { registers: ['spoken'], mustKnow: true, note: 'The #1 spoken sequencer.' }),
  row('story', 'e aí', 'and then', 'E aí ele chegou.', 'And then he arrived.', SPOKEN),
  row('story', 'daí', 'then', 'Daí eu fui embora.', 'Then I left.', { registers: ['spoken', 'regional'], region: 'SP / South' }),
  row('story', 'depois', 'then / after', 'Depois a gente conversa.', "We'll talk later."),
  row('story', 'aliás', 'by the way', 'Aliás, você viu minha chave?', 'By the way, have you seen my key?'),
  row('story', 'falando nisso', 'speaking of which', 'Falando nisso, você viu...?', 'Speaking of which, have you seen...?', SPOKEN),
  row('story', 'enfim', 'anyway', 'Enfim, vamos embora.', "Anyway, let's go.", { registers: ['spoken'], note: 'Closes a topic — different from "de qualquer forma", which means "regardless".' }),

  // Adding and concluding: what you stack on, and what follows from it
  row('adding', 'e', 'and', 'Eu cozinho e ela lava a louça.', 'I cook and she washes the dishes.'),
  row('adding', 'também', 'also / too', 'Eu também quero.', 'I want it too.'),
  row('adding', 'além disso', 'besides / in addition', 'Além disso, está barato.', "Besides, it's cheap.", { note: 'Slightly formal; in speech people often just stack "e".' }),
  row('adding', 'então', 'so', 'Estava quente, então fui de shorts.', 'It was hot, so I wore shorts.'),
  row('adding', 'por isso', "that's why / so", 'Choveu muito, por isso cancelei.', 'It rained a lot, so I cancelled.'),
  row('adding', 'faz com que seja', 'makes it so that', 'A chuva faz com que seja difícil sair.', 'The rain makes it hard to go out.', {
    note: 'Cause → expected outcome; takes the subjunctive.',
  }),

  // Contrasting
  row('contrasting', 'mas', 'but', 'Queria ir, mas estou cansada.', "I wanted to go, but I'm tired."),
  row('contrasting', 'só que', 'but', 'Queria ir, só que tô cansada.', "I wanted to go, but the thing is, I'm tired.", { registers: ['spoken'], mustKnow: true, note: 'The everyday "mas".' }),
  row('contrasting', 'mesmo que', 'even if / although', 'Vou mesmo que chova.', "I'm going even if it rains.", { note: 'Use this, not "embora".' }),
  row('contrasting', 'apesar de', 'despite', 'Fui apesar da chuva.', 'I went despite the rain.', { note: 'Also: "Apesar de cansada, fui."' }),
  row('contrasting', 'mesmo assim', 'even so', 'Estava cansada, mas fui mesmo assim.', 'I was tired, but I went anyway.'),
  row('contrasting', 'se bem que', 'although', 'Fui, se bem que tava cansada.', 'I went, although I was tired.', SPOKEN),
  row('contrasting', 'nem que', 'even if', 'Vou nem que chova.', "I'm going even if it pours.", SPOKEN),
  row('contrasting', 'por mais que', 'however much', 'Por mais que eu tente...', 'However hard I try...'),

  // Explaining and softening
  row('explaining', 'é que', "it's that", 'É que eu tô cansada.', "It's just that I'm tired.", { registers: ['spoken'], mustKnow: true, note: 'Softens explanations.' }),
  row('explaining', 'porque', 'because', 'Fiquei em casa porque estava chovendo.', 'I stayed home because it was raining.'),
  row('explaining', 'por causa de', 'because of', 'Por causa da chuva.', 'Because of the rain.', { registers: ['spoken'], mustKnow: true }),
  row('explaining', 'já que', 'since', 'Já que você tá aqui...', "Since you're here..."),
  row('explaining', 'quer dizer', 'I mean', 'Quer dizer, não foi bem assim.', "I mean, it wasn't quite like that.", SPOKEN),
  row('explaining', 'ou seja', 'that is', 'Ele é vegetariano, ou seja, não come carne.', "He's vegetarian, that is, he doesn't eat meat.", { note: 'A touch more formal than "quer dizer".' }),
  row('explaining', 'tipo', 'like', 'É tipo assim...', "It's like this...", { registers: ['spoken', 'young'] }),
  row('explaining', 'sabe?', 'you know?', 'É difícil, sabe?', "It's hard, you know?", SPOKEN),

  // Time clauses
  row('time', 'antes que', 'before', 'Sai antes que chova.', 'Leave before it rains.'),
  row('time', 'depois que', 'after', 'Depois que eu cheguei...', 'After I arrived...'),
  row('time', 'desde que', 'since', 'Desde que me mudei...', 'Ever since I moved...'),
  row('time', 'enquanto', 'while', 'Enquanto isso, vou cozinhando.', "Meanwhile, I'll keep cooking."),
  row('time', 'quando', 'when', 'Quando cheguei, já tinha saído.', 'When I arrived, he had already left.'),

  // Reactions and check-ins: relief, agreement, are-we-aligned
  row('reactions', 'ainda bem que', 'good thing / luckily', 'Ainda bem que você veio!', 'Good thing you came!'),
  row('reactions', 'né?', 'right?', 'Tá quente hoje, né?', "It's hot today, right?", { registers: ['spoken'], mustKnow: true }),
  row('reactions', 'combinado', 'deal / agreed', 'Combinado!', 'Deal!', SPOKEN),
  row('reactions', 'beleza', 'alright / cool', 'Beleza, até amanhã.', 'Alright, see you tomorrow.', SPOKEN),
]
